import React, { useState, useEffect, useCallback } from 'react';
import { FiUpload, FiImage, FiSettings, FiTrash2, FiSave, FiDroplet, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { Skeleton, SkeletonText } from './Skeleton';
import PageHero from './PageHero';
import PageHelp from './PageHelp';
import { showToast } from './NotificationTray';

function DraggablePreview({ mode, wmUrl, position, onChange, widthPercent, opacity, styleMode, testImage }) {
  const [dragging, setDragging] = useState(false);
  const boxRef = React.useRef(null);
  
  const setPosFromEvent = React.useCallback((clientX, clientY) => {
    if (!boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    onChange({ 
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  }, [onChange]);

  const handleMouseDown = React.useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    setPosFromEvent(e.clientX, e.clientY);
    
    const handleMouseMove = (e) => {
      setPosFromEvent(e.clientX, e.clientY);
    };
    
    const handleMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [setPosFromEvent]);

  const handleTouchStart = React.useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const touch = e.touches[0];
    setPosFromEvent(touch.clientX, touch.clientY);
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      setPosFromEvent(touch.clientX, touch.clientY);
    };
    
    const handleTouchEnd = () => {
      setDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [setPosFromEvent]);

  // Calculate the actual position in pixels
  const left = `${position.x}%`;
  const top = `${position.y}%`;
  
  // Use fixed pixel size to prevent shrinking at edges
  const baseSize = mode === 'landscape' ? 150 : 100;
  const wmSize = (baseSize * widthPercent) / 20; // Scale based on widthPercent
  
  const styleImg = {
    width: `${wmSize}px`, // Use fixed pixel width
    maxWidth: '90%', // Prevent overflow
    opacity: opacity,
    filter: styleMode === 'outline'
      ? 'drop-shadow(0 0 2px #fff) drop-shadow(0 0 2px rgba(0,0,0,.6))'
      : (styleMode === 'shadow' ? 'drop-shadow(0 2px 4px rgba(0,0,0,.6))' : 'none')
  };

  const backgroundStyle = testImage 
    ? { backgroundImage: `url(${testImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { 
        backgroundImage: mode === 'landscape' 
          ? 'linear-gradient(45deg, #81a49f 0%, #304849 100%)' 
          : 'linear-gradient(135deg, #fcefb3 0%, #81a49f 100%)'
      };

  return (
    <div
      ref={boxRef}
      className={`wm-preview-box ${mode}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{ cursor: dragging ? 'grabbing' : 'grab', ...backgroundStyle }}
    >
      <div className="wm-drag" style={{ left, top }}>
        {wmUrl ? (
          <img src={wmUrl} alt="Watermark" style={styleImg} />
        ) : (
          <div style={{ 
            padding: '8px 12px', 
            background: 'rgba(255,255,255,0.9)', 
            borderRadius: 4,
            color: 'rgba(0,0,0,0.8)',
            fontWeight: 'bold'
          }}>
            WM
          </div>
        )}
      </div>
    </div>
  );
}

export default function Watermark() {
  const [wm, setWm] = useState({ 
    exists: false, 
    previewUrl: null, 
    config: { 
      portraitPosition: 'center', 
      landscapePosition: 'center', 
      opacity: 0.35, 
      portraitXPercent: 50, 
      portraitYPercent: 50, 
      landscapeXPercent: 50, 
      landscapeYPercent: 50, 
      widthPercent: 20 
    } 
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewStyle, setPreviewStyle] = useState('shadow');
  const [testImage, setTestImage] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [canUseWatermarks, setCanUseWatermarks] = useState(false);
  const authToken = window?.localStorage?.getItem('auth_token');
  const watermarkPreviewUrl = wm.previewUrl && authToken
    ? wm.previewUrl
    : wm.previewUrl;

  useEffect(() => {
    loadWatermark();
    checkSubscription();
  }, []);

  async function checkSubscription() {
    try {
      const res = await fetch('/api/subscription', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
        setCanUseWatermarks(data.limits?.canUseWatermarks || false);
      }
    } catch (e) {
      console.error('Failed to check subscription:', e);
    }
  }

  async function loadWatermark() {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/watermark', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWm(data);
        setHasChanges(false); // Reset changes flag when loading
      }
    } catch (e) {
      console.error('Failed to load watermark:', e);
    } finally {
      setLoading(false);
    }
  }

  async function uploadWatermark(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    
    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 100);
    
    try {
      const res = await fetch('/api/settings/watermark', { 
        method: 'POST', 
        credentials: 'include', 
        body: fd 
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (res.ok) {
        const data = await res.json();
        setWm((prev) => ({ ...prev, exists: true, previewUrl: data.previewUrl }));
        setTimeout(() => setUploadProgress(0), 500);
        showToast('Watermark uploaded', 'PNG saved and ready to use.', 'success');
      }
    } catch (e) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      console.error('Upload failed:', e);
      showToast('Upload failed', 'Please try a smaller PNG.', 'error');
    }
  }

  async function removeWatermark() {
    if (!confirm('Remove watermark image?')) return;
    try {
      const res = await fetch('/api/settings/watermark', { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      if (res.ok) {
        setWm((prev) => ({ ...prev, exists: false, previewUrl: null }));
        showToast('Watermark removed', 'Images will no longer be watermarked.', 'info');
      }
    } catch (e) {
      console.error('Remove failed:', e);
    }
  }

  // Helper function to update watermark config and track changes
  const updateWmConfig = (updater) => {
    setWm(updater);
    setHasChanges(true);
  };

  async function saveConfig() {
    setSaving(true);
    try {
      const res = await fetch('/api/settings/watermark', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wm.config),
      });
      if (res.ok) {
        // Show success feedback and reset changes flag
        setHasChanges(false);
        showToast('Watermark settings saved', 'Applied to new watermarked renders.', 'success');
      }
    } catch (e) {
      console.error('Save failed:', e);
      showToast('Save failed', 'Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  function uploadTestImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setTestImage(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  function clamp01(v) {
    const n = Number(v);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  function clampRange(v, min, max) {
    const n = Number(v);
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, Math.round(n)));
  }

  const getWatermarkPosition = (isLandscape) => {
    const pos = isLandscape ? wm.config.landscapePosition : wm.config.portraitPosition;
    const xPercent = isLandscape ? wm.config.landscapeXPercent : wm.config.portraitXPercent;
    const yPercent = isLandscape ? wm.config.landscapeYPercent : wm.config.portraitYPercent;
    
    if (pos === 'custom') {
      return {
        position: 'absolute',
        left: `${xPercent}%`,
        top: `${yPercent}%`,
        transform: 'translate(-50%, -50%)',
      };
    }
    
    const positions = {
      'top-left': { top: '10px', left: '10px' },
      'top-center': { top: '10px', left: '50%', transform: 'translateX(-50%)' },
      'top-right': { top: '10px', right: '10px' },
      'center-left': { top: '50%', left: '10px', transform: 'translateY(-50%)' },
      'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
      'center-right': { top: '50%', right: '10px', transform: 'translateY(-50%)' },
      'bottom-left': { bottom: '10px', left: '10px' },
      'bottom-center': { bottom: '10px', left: '50%', transform: 'translateX(-50%)' },
      'bottom-right': { bottom: '10px', right: '10px' },
    };
    
    return { position: 'absolute', ...positions[pos] };
  };

  if (loading) {
    return (
      <div className="watermark-page">
        <PageHero
          eyebrow="Brand protection"
          title="Watermark"
        />
        <div className="page-toolbar">
          <PageHelp
            title="How to use Watermark"
            description="Upload a logo and control how it appears on shared images."
            tips={[
              'Upload a transparent PNG or SVG for best results.',
              'Adjust size, opacity, and placement in the preview.',
              'Save once it looks right to apply it to new shares.'
            ]}
            subject="Photo Harbor Help - Watermark"
          />
        </div>
        <div className="mobile-content-wrapper">
          <div className="watermark-tabs">
            <SkeletonText lines={1} width="100px" />
            <SkeletonText lines={1} width="150px" />
        </div>

        <div className="watermark-content">
          <div className="upload-section">
            <div className="upload-card">
              <SkeletonText lines={1} width="150px" />
              <SkeletonText lines={1} width="80%" />
              <div style={{ marginTop: '2rem' }}>
                <Skeleton width="100%" height="200px" />
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Show upgrade prompt if user doesn't have watermark access
  if (subscription && !canUseWatermarks) {
    return (
      <div className="watermark-page">
        <PageHero
          eyebrow="Brand protection"
          title="Watermark"
        />
        <div className="page-toolbar">
          <PageHelp
            title="How to use Watermark"
            description="Upload a logo and control how it appears on shared images."
            tips={[
              'Upload a transparent PNG or SVG for best results.',
              'Adjust size, opacity, and placement in the preview.',
              'Save once it looks right to apply it to new shares.'
            ]}
            subject="Photo Harbor Help - Watermark"
          />
        </div>
        <div className="mobile-content-wrapper">
        <div className="upgrade-prompt">
          <div className="upgrade-card">
            <FiImage className="upgrade-icon" />
            <h3>Professional Feature</h3>
            <p>Watermarking is available with Professional, Advanced, and Studio plans.</p>
            <div className="upgrade-benefits">
              <ul>
                <li>Custom watermark on all shared images</li>
                <li>Flexible positioning options</li>
                <li>Adjustable opacity settings</li>
                <li>Portrait and landscape configurations</li>
              </ul>
            </div>
            <p className="current-plan">Current plan: <strong>{subscription.tierName}</strong></p>
            <button 
              className="button-primary upgrade-button"
              onClick={() => {
                // Open billing/upgrade page - you could navigate to a dedicated page or open a modal
                window.location.href = '/settings';
              }}
            >
              Upgrade to Professional
            </button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="watermark-page">
      <PageHero
        eyebrow="Brand protection"
        title="Watermark"
      />
      <div className="page-toolbar">
        <PageHelp
          title="How to use Watermark"
          description="Upload a logo and control how it appears on shared images."
          tips={[
            'Upload a transparent PNG or SVG for best results.',
            'Adjust size, opacity, and placement in the preview.',
            'Save once it looks right to apply it to new shares.'
          ]}
          subject="Photo Harbor Help - Watermark"
        />
      </div>
      <div className="mobile-content-wrapper">
        <div className="watermark-hero">
          <div className="watermark-hero-copy">
            <h3 className="watermark-hero-title">Watermark status</h3>
            <p className="watermark-hero-description">Set a watermark to protect shared images and client galleries.</p>
            <div className="watermark-hero-actions">
              <span className={`watermark-status-pill ${wm.exists ? 'is-active' : 'is-inactive'}`}>
                {wm.exists ? <FiCheckCircle /> : <FiAlertCircle />}
                {wm.exists ? 'Active watermark' : 'No watermark set'}
              </span>
              {hasChanges && (
                <button className="button-primary" onClick={saveConfig} disabled={saving}>
                  <FiSave /> {saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>
          <div className="watermark-hero-card">
            <div className="watermark-hero-card-top">
              <span>Current watermark</span>
              <span className="watermark-hero-note">
                {wm.exists ? 'Used on share links + client galleries' : 'Upload a PNG to get started'}
              </span>
            </div>
            <div className="watermark-hero-preview">
              {wm.exists ? (
                <img src={watermarkPreviewUrl} alt="Current watermark preview" />
              ) : (
                <div className="watermark-hero-placeholder">
                  <FiUpload size={32} />
                  <span>Drop in a PNG logo</span>
                </div>
              )}
            </div>
            <div className="watermark-hero-foot">
              Transparent PNGs preserve clean edges on any photo.
            </div>
          </div>
        </div>

      <div className="settings-tabs" role="tablist" aria-label="Watermark">
        <button type="button" role="tab" className={`tab-button ${activeTab === 'upload' ? 'is-active' : ''}`} aria-selected={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
          <FiUpload /> Upload
        </button>
        <button type="button" role="tab" className={`tab-button ${activeTab === 'position' ? 'is-active' : ''}`} aria-selected={activeTab === 'position'} onClick={() => setActiveTab('position')} disabled={!wm.exists}>
          <FiSettings /> Position & Preview
        </button>
      </div>

      <div className="watermark-content">
        {activeTab === 'upload' && (
          <div className="upload-section">
            <div className="upload-card">
              <h3>Watermark Image</h3>
              <p>Upload a PNG image with transparency for best results</p>
              
              {!wm.exists ? (
                <div className="upload-area">
                  <input 
                    type="file" 
                    accept="image/png,image/*" 
                    onChange={uploadWatermark} 
                    id="watermark-upload"
                  />
                  <label htmlFor="watermark-upload" className="upload-label">
                    <FiUpload size={48} />
                    <span>Click to upload watermark</span>
                    <small>Recommended: PNG with transparent background</small>
                  </label>
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="current-watermark">
                  <img src={watermarkPreviewUrl} alt="Current watermark" />
                  <div className="watermark-actions">
                    <button className="button-secondary" onClick={() => document.getElementById('watermark-replace').click()}>
                      <FiUpload /> Replace
                    </button>
                    <button className="button-danger" onClick={removeWatermark}>
                      <FiTrash2 /> Remove
                    </button>
                    <input 
                      type="file" 
                      accept="image/png,image/*" 
                      onChange={uploadWatermark} 
                      id="watermark-replace"
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="watermark-tips">
              <h4>Tips for Best Results</h4>
              <ul>
                <li>Use a PNG file with transparent background</li>
                <li>Keep the image size under 1MB for faster loading</li>
                <li>Use a resolution of at least 500px for clarity</li>
                <li>White or light colored logos work best on photos</li>
                <li>Test your watermark on both light and dark images</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'position' && wm.exists && (
          <div className="position-section">
            {/* Test Image Controls */}
            <div className="test-image-controls">
              <h3>Test Image (Optional)</h3>
              <p>Upload a test photo to preview your watermark on a real image</p>
              <div className="test-controls-row">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={uploadTestImage} 
                  id="test-image-upload"
                />
                <label htmlFor="test-image-upload" className="button-secondary">
                  <FiImage /> Upload Test Image
                </label>
                
                {testImage && (
                  <button 
                    className="button-minimal"
                    onClick={() => setTestImage(null)}
                  >
                    Clear Test Image
                  </button>
                )}
              </div>
            </div>

            {/* Universal Controls */}
            <div className="universal-controls">
              <h3>Universal Settings</h3>
              <p>These settings apply to both portrait and landscape orientations</p>
              <div className="universal-controls-grid">
                <div className="control-item">
                  <label>
                    <span>Opacity: {Math.round((wm.config.opacity || 0.35) * 100)}%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="1" 
                      step="0.05"
                      value={wm.config.opacity || 0.35}
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, opacity: parseFloat(e.target.value) }}))}
                    />
                  </label>
                </div>
                <div className="control-item">
                  <label>
                    <span>Size: {wm.config.widthPercent || 20}% of image width</span>
                    <input 
                      type="range" 
                      min="5" 
                      max="50" 
                      step="1"
                      value={wm.config.widthPercent || 20}
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, widthPercent: Number(e.target.value) }}))}
                    />
                  </label>
                </div>
                <div className="control-item">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span>Style</span>
                    <div className="segmented-control" role="tablist" aria-label="Watermark Style">
                      <button className={previewStyle === 'none' ? 'is-active' : ''} onClick={() => setPreviewStyle('none')}>None</button>
                      <button className={previewStyle === 'outline' ? 'is-active' : ''} onClick={() => setPreviewStyle('outline')}>Outline</button>
                      <button className={previewStyle === 'shadow' ? 'is-active' : ''} onClick={() => setPreviewStyle('shadow')}>Shadow</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Grid */}
            <div className="preview-grid">
              <div className="preview-item">
                <h4>Portrait Photos</h4>
                <div className="segmented-control" role="tablist" aria-label="Portrait Presets" style={{ marginBottom: 8 }}>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 15, portraitYPercent: 15, portraitPosition: 'top-left' }}))}>TL</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 50, portraitYPercent: 15, portraitPosition: 'top-center' }}))}>TC</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 85, portraitYPercent: 15, portraitPosition: 'top-right' }}))}>TR</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 15, portraitYPercent: 50, portraitPosition: 'center-left' }}))}>CL</button>
                  <button className={((wm.config.portraitXPercent||50)===50 && (wm.config.portraitYPercent||50)===50) ? 'is-active' : ''} onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 50, portraitYPercent: 50, portraitPosition: 'center' }}))}>C</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 85, portraitYPercent: 50, portraitPosition: 'center-right' }}))}>CR</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 15, portraitYPercent: 85, portraitPosition: 'bottom-left' }}))}>BL</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 50, portraitYPercent: 85, portraitPosition: 'bottom-center' }}))}>BC</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 85, portraitYPercent: 85, portraitPosition: 'bottom-right' }}))}>BR</button>
                </div>
                <DraggablePreview 
                  mode="portrait"
                  wmUrl={watermarkPreviewUrl}
                  position={{ x: wm.config.portraitXPercent || 50, y: wm.config.portraitYPercent || 50 }}
                  widthPercent={wm.config.widthPercent || 20}
                  opacity={wm.config.opacity || 0.35}
                  styleMode={previewStyle}
                  testImage={testImage}
                  onChange={(pos) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: pos.x, portraitYPercent: pos.y }}))}
                />
                <div className="position-coords">
                  <label>
                    <span>X: {Math.round(wm.config.portraitXPercent || 50)}%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={wm.config.portraitXPercent || 50} 
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: Number(e.target.value) }}))} 
                    />
                  </label>
                  <label>
                    <span>Y: {Math.round(wm.config.portraitYPercent || 50)}%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={wm.config.portraitYPercent || 50} 
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitYPercent: Number(e.target.value) }}))} 
                    />
                  </label>
                  <button 
                    className="button-minimal"
                    onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, portraitXPercent: 50, portraitYPercent: 50 }}))}
                  >
                    Center
                  </button>
                </div>
              </div>

              <div className="preview-item">
                <h4>Landscape Photos</h4>
                <div className="segmented-control" role="tablist" aria-label="Landscape Presets" style={{ marginBottom: 8 }}>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 15, landscapeYPercent: 15, landscapePosition: 'top-left' }}))}>TL</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 50, landscapeYPercent: 15, landscapePosition: 'top-center' }}))}>TC</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 85, landscapeYPercent: 15, landscapePosition: 'top-right' }}))}>TR</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 15, landscapeYPercent: 50, landscapePosition: 'center-left' }}))}>CL</button>
                  <button className={((wm.config.landscapeXPercent||50)===50 && (wm.config.landscapeYPercent||50)===50) ? 'is-active' : ''} onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 50, landscapeYPercent: 50, landscapePosition: 'center' }}))}>C</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 85, landscapeYPercent: 50, landscapePosition: 'center-right' }}))}>CR</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 15, landscapeYPercent: 85, landscapePosition: 'bottom-left' }}))}>BL</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 50, landscapeYPercent: 85, landscapePosition: 'bottom-center' }}))}>BC</button>
                  <button onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 85, landscapeYPercent: 85, landscapePosition: 'bottom-right' }}))}>BR</button>
                </div>
                <DraggablePreview 
                  mode="landscape"
                  wmUrl={watermarkPreviewUrl}
                  position={{ x: wm.config.landscapeXPercent || 50, y: wm.config.landscapeYPercent || 50 }}
                  widthPercent={wm.config.widthPercent || 20}
                  opacity={wm.config.opacity || 0.35}
                  styleMode={previewStyle}
                  testImage={testImage}
                  onChange={(pos) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: pos.x, landscapeYPercent: pos.y }}))}
                />
                <div className="position-coords">
                  <label>
                    <span>X: {Math.round(wm.config.landscapeXPercent || 50)}%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={wm.config.landscapeXPercent || 50} 
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: Number(e.target.value) }}))} 
                    />
                  </label>
                  <label>
                    <span>Y: {Math.round(wm.config.landscapeYPercent || 50)}%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={wm.config.landscapeYPercent || 50} 
                      onChange={(e) => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeYPercent: Number(e.target.value) }}))} 
                    />
                  </label>
                  <button 
                    className="button-minimal"
                    onClick={() => updateWmConfig(prev => ({ ...prev, config: { ...prev.config, landscapeXPercent: 50, landscapeYPercent: 50 }}))}
                  >
                    Center
                  </button>
                </div>
              </div>
            </div>

            <div className="save-section">
              <button className="button-primary" onClick={saveConfig} disabled={saving || !hasChanges}>
                <FiSave /> {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
